import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { useTranslation } from 'react-i18next';
import { Session } from 'types/Session';
import { SessionAgenda, SessionAgendaContentFormat } from 'types/SessionAgenda';
import { usePanel } from 'contexts/panelContext';
import { Pair } from 'types/Pair';
import { RouterUrlParams } from 'App';
import { ArticlePresenterDialog } from 'components/knowledgeBase/ArticlePresenterDialog';
import { contentParser } from 'utils/contentParser';

type AgendaSubpageProps = {
	session: Session;
	pair: Pair;
	sessionAgenda?: SessionAgenda;
	prevSession?: Session;
	nextSession?: Session;
	handlers?: Record<string, () => void>;
};

export const AgendaSubpage = ({
	session,
	pair,
	sessionAgenda,
	prevSession,
	nextSession,
	handlers,
}: AgendaSubpageProps) => {
	const { t } = useTranslation();
	const { organizationName, programName, type, pairId } = useParams() as RouterUrlParams;

	const [currentArticleId, setCurrentArticleId] = useState<number | null>(null);

	const { menteeApplicationId, mentorApplicationId, menteeMembershipId, mentorMembershipId } = pair;
	const { currentApplicationData, currentProgramMembership, panelLinkPrefix } = usePanel();
	const secondApplicationId =
		currentApplicationData?.id === menteeApplicationId ? mentorApplicationId : menteeApplicationId;
	const secondMembershipId =
		currentProgramMembership?.id === menteeMembershipId ? mentorMembershipId : menteeMembershipId;

	const knowledgeBaseLinkPrefix = `panel/${organizationName}/${programName}/${type}/knowledge-base`;

	const parser = contentParser({
		handlers: {
			...handlers,
			onArticleLinkClick: setCurrentArticleId,
		},
		replaces: {
			panelPrefix: panelLinkPrefix,
			organization: organizationName,
			program: programName,
			userRole: type,
			pairId,
			sessionId: session?.id || 0,
			nextSessionId: nextSession?.id || 0,
			prevSessionId: prevSession?.id || 0,
			applicationId: currentApplicationData?.id || 0,
			secondApplicationId: secondApplicationId || 0,
			mentorApplicationId: mentorApplicationId || 0,
			menteeApplicationId: menteeApplicationId || 0,
			programMembershipId: currentProgramMembership?.id || 0,
			secondMembershipId: secondMembershipId || 0,
			mentorMembershipId: mentorMembershipId || 0,
			menteeMembershipId: menteeMembershipId || 0,
		},
	});

	const renderContent = () => {
		if (sessionAgenda && sessionAgenda.content) {
			switch (sessionAgenda.format) {
				case SessionAgendaContentFormat.MD:
					return <ReactMarkdown>{sessionAgenda.content}</ReactMarkdown>;
				case SessionAgendaContentFormat.HTML:
				default:
					return parser.parse(sessionAgenda.content);
			}
		}
		return <span>brak</span>;
	};

	const contentParserOptions = {
		handlers: {
			onArticleLinkClick: (articleId: number) => {
				setCurrentArticleId(articleId);
			},
		},
		replaces: {
			panelPrefix: panelLinkPrefix,
			organization: organizationName,
			program: programName,
			usertype: type,
		},
		disableFormat: true,
	};

	return (
		<>
			<ArticlePresenterDialog
				articleId={currentArticleId}
				visible={Boolean(currentArticleId)}
				onHide={() => {
					setCurrentArticleId(null);
				}}
				contentParserOptions={contentParserOptions}
				knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
			/>
			<div className="flex flex-column gap-2">
				<h2 className="my-5">{t('userPanel.sessionsNew.proposedAgenda')}</h2>
				<div className="agenda-content w-full">{renderContent()}</div>
			</div>
		</>
	);
};
